import React from 'react';
import PropTypes from 'prop-types';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import get from 'lodash/get';
import { currency } from 'utility/format';
import LWTypography from 'components/legacy/LWTypography';
import PageOverview from 'components/legacy/PageOverview';
import LWButton from 'components/legacy/LWButton';
import isFQDN from 'validator/es/lib/isFQDN';
import styled from 'styled-components';
import { useNavTitle } from 'components/LeftHandLayout';
import useBrandName from 'utility/hooks/useBrandName';
import useSendHubspotEvent from 'utility/gtm/hubspot/useEvent';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';
import CreateTransferForm from './CreateTransferForm';

const SStepper = styled(Stepper)`
	.MuiStep-completed {
		.MuiStepIcon-completed {
			fill: ${(p) => p.theme.palette.grey[300]};
		}
		.MuiStepLabel-iconContainer {
			margin-right: ${(p) => p.theme.spacing(1)}px;
			border-radius: 50%;
			overflow: hidden;
			background: ${(p) => p.theme.palette.primary.dark};
			padding: 0;
		}
	}
`;

function getSteps() {
	return ['Unlock Domain', 'EPP Code', 'Authorize Transfer'];
}

const CreateTransfer = ({
	changeFieldValue,
	clearIncomingTransfer,
	createIncomingTransfer,
	created,
	displayError,
	fetchTldDetails,
	tldDetails,
	form,
	history,
	resetForm,
}) => {
	useNavTitle('Domain Transfer');
	useDocumentTitle('Domain Transfer');
	const brandName = useBrandName();

	const [activeStep, setActiveStep] = React.useState(0);
	const steps = getSteps();
	const isFinalStep = activeStep === steps.length - 1;

	const sendDomainEvent = useSendHubspotEvent('domain');

	function handleNext() {
		setActiveStep((prevActiveStep) => {
			if (prevActiveStep + 1 === steps.length - 1) {
				fetchTldDetails(form.values);
			}
			return prevActiveStep + 1;
		});
	}

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	const handleSubmit = () => {
		// This page is going to be redesigned, so if you see merge conflicts, know that
		// we need to keep this GTM Event
		sendDomainEvent({ serverHostName: form.values.domain });
		createIncomingTransfer(form.values);
	};

	// Disabling lint rule here. This is only because this is a legacy
	// component and refactoring to proper pattern is not worth risk or effort
	// eslint-disable-next-line wallaby/no-nested-render-functions
	const getStepContent = (step) => {
		switch (step) {
			case 0:
				return `To start transferring a domain to ${brandName}, you will need to log into your current registrar and unlock your domain. You will then need to request a transfer secret or transfer code from your registrar, sometimes called an EPP code.
				
				This code will be emailed to the registrant email address that is currently associated with the domain registration. Once you have this code, click Next to begin the transfer process.`;
			case 1:
				return <CreateTransferForm initialValues={form.values} />;
			case 2:
				return (
					<>
						<Box pb={2}>
							When transferring your domain, the expiration will automatically
							be extended by one year, and as such, will incur one year of
							domain registration charges.
						</Box>
						<div>
							<LWTypography bold inline>
								Domain:{' '}
							</LWTypography>
							{form.values ? form.values.domain : null}
						</div>
						<Box pb={2}>
							<LWTypography bold inline>
								Price:{' '}
							</LWTypography>
							{!tldDetails.isLoading && tldDetails.hasData ? (
								currency(Number(tldDetails.transferPrice))
							) : (
								<CircularProgress size={12} />
							)}
						</Box>
						<div>
							If a credit card is on file, the card will be authorized for the
							amount listed, and the payment will be finalized once the transfer
							is complete. Otherwise, the account will need to have an existing
							balance to cover the cost of the transfer. Funds can be added on
							the Account Billing Page.
						</div>
					</>
				);
			default:
				return 'Unknown step';
		}
	};

	React.useEffect(() => {
		if (created.hasData) {
			clearIncomingTransfer();
			resetForm();
			history.push('/domain');
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [created.hasData]);

	React.useEffect(() => {
		if (created.hasError) {
			setActiveStep(0);
			clearIncomingTransfer();
			resetForm();
			displayError('Failed to initiate domain transfer');
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [created.hasError]);

	React.useEffect(() => {
		changeFieldValue('eppCode', null);
		changeFieldValue('domain', null);
	}, [changeFieldValue]);
	const domain = get(form, 'values.domain');
	const eppCode = get(form, 'values.eppCode');

	return (
		<PageOverview name="Domain Transfer" favoritesDisplayName="Domain Transfer">
			<SStepper activeStep={activeStep} orientation="vertical">
				{steps.map((label, index) => {
					return (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
							<StepContent>
								<LWTypography>{getStepContent(index)}</LWTypography>
								<Box alignItems="center" display="flex" mt={2}>
									{activeStep !== 0 && (
										<LWButton onClick={handleBack} color="tertiary">
											Back
										</LWButton>
									)}
									<LWButton
										BoxProps={{ ml: 2 }}
										variant="contained"
										disabled={
											(activeStep === 1 &&
												(!domain ||
													!eppCode ||
													eppCode === '' ||
													domain === '')) ||
											(domain && !isFQDN(domain))
										}
										onClick={isFinalStep ? handleSubmit : handleNext}
										type={isFinalStep ? 'submit' : 'button'}
										isLoading={created.isLoading}
									>
										{isFinalStep ? 'Authorize Transfer' : 'Next'}
									</LWButton>
								</Box>
							</StepContent>
						</Step>
					);
				})}
			</SStepper>
			{activeStep === steps.length && (
				<Box p={3}>
					<Paper square>
						<LWTypography>
							All steps completed - you&apos;re finished
						</LWTypography>
						<LWButton color="tertiary" onClick={handleReset}>
							Reset
						</LWButton>
					</Paper>
				</Box>
			)}
		</PageOverview>
	);
};

CreateTransfer.propTypes = {
	createIncomingTransfer: PropTypes.func.isRequired,
	created: PropTypes.shape({
		isLoading: PropTypes.bool,
		hasData: PropTypes.bool,
	}),
	fetchTldDetails: PropTypes.func.isRequired,
	tldDetails: PropTypes.object,
	form: PropTypes.object,
	history: PropTypes.object,
};

export default CreateTransfer;
